import _ from "lodash";
import {
  MarketingCampaignType,
  Offer,
  ProductDiscountMarketingCampaignConfig,
  ThresholdMarketingCampaignConfig,
  GetOneMarketingCampaignConfig,
  BuyNGetMMarketingCampaignConfig
} from "../../../../1_types/genericTypes";
import { sendGoogleAnalytics } from "../../../../api";
import { CUSTOMER_ADDRESS_CHECK_VALUES } from "../../../../redux/reducers/componentReducer";
import { isCustomerAddressDefined } from "../../../../utils/customer-address-and-shops";
import { showAddressModal } from "../../../../redux/actions";
import {
  isChatbot,
  isQrCode,
  isStringNotNull,
  displayPriceFormat,
} from "../../../../utils";
import { analyticsEvents } from "../../../../utils/firebaseAnalytics";
import { OFFER_SECTION_KEY } from "../../../../constants";
import { openMessageModal } from "../../../../utils/common";
import { offerAppliesToProduct } from "../../../../utils/marketingCampaign";

export const handleClickCard = ({
  dispatch,
  setShowModal,
  productDisabled,
  customerAddressCheck,
  customerAddress,
  orders,
  multiOrder,
  key,
  price,
  position,
  section,
  name,
}) => {
  if (productDisabled) return;

  if (
    !isChatbot() &&
    !isQrCode() &&
    (customerAddressCheck !== CUSTOMER_ADDRESS_CHECK_VALUES.VALID ||
      !isCustomerAddressDefined(customerAddress))
  ) {
    dispatch(showAddressModal(true));
    return;
  }

  if (!_.isEmpty(orders) && !multiOrder) {
    openMessageModal(
      `Vous ne pouvez pas ajouter de produits car vous avez déjà ${_.size(orders) > 1 ? `${_.size(orders)} commandes` : "une commande"
      } en cours`,
    );
    return;
  }

  sendGoogleAnalytics("view_item", {
    items: [{ id: key, name, price, position, category: section.name }],
    currency: "EUR",
    value: price,
  });

  analyticsEvents.logViewItem(
    "productView",
    key,
    name,
    price,
    section.name,
    position,
    "EUR",
  );
  setShowModal(true);
};

export const getNbProductForAnOriginalProduct = (
  products,
  originalSectionKey,
  productKey,
  productMarketingCampaignKey,
) => {
  let nbTotal = 0;
  _.map(products, (product) => {
    const sameCampaignKey =
      (product.productMarketingCampaignKey ?? null) ===
      (productMarketingCampaignKey ?? null);
    if (
      originalSectionKey === product.sectionKey &&
      productKey === product.key &&
      !product.isLoyaltyProduct &&
      sameCampaignKey
    ) {
      nbTotal += product.nb;
    }
  });
  return nbTotal;
};

export const getTooltipTextFromOffer = (
  offer: Offer,
): { title: string; description?: string } | null => {
  if (!offer) return null;
  switch (offer.type) {
    case MarketingCampaignType.BUY_ONE_GET_ONE_PRODUCT:
      return {
        title: "1 acheté = 1 offert",
        description: "Achetez un produit et recevez-en un autre gratuitement.",
      };
    case MarketingCampaignType.BUY_ONE_GET_ONE_DIFFERENT_PRODUCT:
      return {
        title: "1 acheté = 1 autre offert",
        description:
          "Achetez un produit et recevez un produit différent gratuitement.",
      };
    case MarketingCampaignType.PRODUCT_DISCOUNT:
      return {
        title: `${(offer.config as ProductDiscountMarketingCampaignConfig).discount
          .value
          }% de réduction`,
        description: "Réduction produit",
      };
    case MarketingCampaignType.PRODUCT_DISCOUNT_AFTER_THRESHOLD:
      const config = offer.config as ThresholdMarketingCampaignConfig;
      return {
        title: `-${config.discount.value}% à partir de ${config.threshold}€`,
        description: "Réduction après seuil",
      };
    case MarketingCampaignType.BUY_N_GET_M_CHEAPEST_AT_DISCOUNT: {
      const bngmConfig = offer.config as BuyNGetMMarketingCampaignConfig;
      const numProductsPaid = bngmConfig.numProductsPaid ?? 0;
      const numProductsDiscounted = bngmConfig.numProductsDiscounted ?? 0;
      if (numProductsDiscounted === 1) {
        return { title: `${numProductsPaid} acheté${numProductsPaid > 1 ? 's' : ''}, 1 offert` }
      }
      return { title: `${numProductsPaid} acheté${numProductsPaid > 1 ? 's' : ''}, ${numProductsDiscounted} offert${numProductsDiscounted > 1 ? 's' : ''}` };
    }
    default:
      return null;
  }
};

export const getCurrentOfferForProduct = (
  productKey: string,
  sectionKey: string,
  thresholdOffer: Offer,
  productOffers: Offer[],
  isOfferSection: boolean = true,
): Offer => {
  if (!isOfferSection) {
    return null;
  }
  if (
    thresholdOffer &&
    offerAppliesToProduct(thresholdOffer, productKey, sectionKey)
  ) {
    return thresholdOffer;
  }

  const currentOffer =
    _.find(
      productOffers,
      (productOffer: Offer) =>
        !!_.find(
          productOffer.config.products,
          (product) =>
            product.productKey === productKey &&
            (product.productSectionKey === sectionKey ||
              sectionKey === OFFER_SECTION_KEY),
        ),
    ) ?? null;

  if (
    currentOffer &&
    [
      MarketingCampaignType.BUY_ONE_GET_ONE_DIFFERENT_PRODUCT,
      MarketingCampaignType.BUY_ONE_GET_ONE_PRODUCT,
    ].includes(currentOffer.type) &&
    !isOfferSection
  ) {
    return null;
  }

  return currentOffer;
};

export const getOfferProductsNbInPendingOrderBasket = (
  pendingOrderBasket: any,
  offerKey: string,
): number => {
  if (!isStringNotNull(offerKey)) {
    return 0;
  }
  return Object.values(pendingOrderBasket || {})
    .filter(
      (basketItem: any) => basketItem?.productMarketingCampaignKey === offerKey,
    )
    .reduce(
      (sum: number, basketItem: { nb?: number }) => sum + (basketItem?.nb ?? 1),
      0,
    ) as number;
};
export const getDisabledMessage = (
  productOffer,
  potentialThreshold,
  isDisabled,
  isLimitReached,
  potentialLimit,
  thresholdNotReached,
) => {
  if (isDisabled) {
    return {
      text: "Produit indisponible",
      severity: "error",
    };
  }

  if (isLimitReached) {
    return {
      text: `Limitée à ${potentialLimit} article${Number(potentialLimit) > 1 ? "s" : ""}`,
      severity: "error",
    };
  }

  if (thresholdNotReached) {
    return {
      text: `Disponible dès ${displayPriceFormat(potentialThreshold, false, true)} d’achat`,
      severity: "warning",
    };
  }
  if (potentialLimit > 0) {
    return {
      text: `Limitée à ${(productOffer?.config as GetOneMarketingCampaignConfig)?.limit
        } utilisation${(productOffer?.config as GetOneMarketingCampaignConfig)?.limit > 1 ? "s" : ""} par panier`,
      severity: "warning",
    };
  }
};
